import { PoTableColumn } from '@po-ui/ng-components';

import { sanitizeStringArray } from './ui-prefs.util';

/**
 * O que o gerenciador de colunas do po-table deixa o usuário mudar, num formato que
 * sobrevive a uma nova versão da tela.
 *
 * Guardamos `property`, nunca a coluna inteira: largura, tipo e rótulo são definição da
 * tela, e restaurá-los do navegador congelaria a tabela numa versão antiga — uma coluna
 * que ganhou 40px ou virou `cellTemplate` voltaria como era no dia em que o usuário
 * mexeu no gerenciador.
 */
export interface TableColumnsPref {
  /** Propriedades visíveis, na ordem em que aparecem na tabela. */
  order: string[];
  /**
   * Propriedades que o usuário desligou.
   *
   * Guardada explicitamente, e não deduzida de "não está em `order`": sem ela, uma
   * coluna NOVA (que a tela passou a ter depois de o usuário salvar) seria
   * indistinguível de uma desligada, e nasceria escondida — o usuário nunca veria o
   * recurso que acabamos de entregar, e não teria motivo para procurá-lo no
   * gerenciador.
   */
  hidden: string[];
  /** Propriedades fixadas (o alfinete), sempre à esquerda da tabela. */
  fixed: string[];
}

/** Recusa qualquer coisa que não tenha as três listas de strings. */
export function sanitizeColumnsPref(raw: unknown): TableColumnsPref | null {
  if (!raw || typeof raw !== 'object') { return null; }

  const value = raw as Partial<TableColumnsPref>;
  const pref: TableColumnsPref = {
    order: sanitizeStringArray(value.order),
    hidden: sanitizeStringArray(value.hidden),
    fixed: sanitizeStringArray(value.fixed)
  };

  // Sem nenhuma coluna visível não há preferência a aplicar — e aplicar assim mesmo
  // deixaria a tabela vazia, sem nada em que clicar para voltar atrás.
  return pref.order.length ? pref : null;
}

/**
 * Reconstrói as colunas da tela a partir da preferência salva.
 *
 * `base` continua sendo a fonte da verdade de tudo que não é do usuário (rótulo, tipo,
 * largura, `sortable`): a preferência só reordena, esconde e fixa. Propriedades salvas
 * que não existem mais em `base` são ignoradas, e colunas de `base` que a preferência
 * não conhece entram visíveis no fim — ver `hidden`.
 *
 * Devolve sempre objetos novos: o gerenciador do po-table escreve `fixed` direto nos
 * objetos de coluna que recebe, e reaproveitar os de `base` faria essa escrita vazar
 * para a definição da tela (que costuma ser `readonly` justamente por isso).
 */
export function applyColumnsPref(base: PoTableColumn[], pref: TableColumnsPref | null): PoTableColumn[] {
  const copies = base.map(column => ({ ...column }));
  if (!pref) { return copies; }

  const byProperty = new Map(copies.map(column => [column.property, column]));
  const ordered: PoTableColumn[] = [];
  // Por `property`, e não por identidade do objeto: as colunas de `order` entram como
  // cópia (para afirmar o `visible`), então comparar referência não veria a repetição.
  const placed = new Set<string | undefined>();

  // `visible: true` explícito, e não a coluna como veio: uma coluna que a TELA define
  // como `visible: false` (opt-in) e que o usuário ligou está em `order` — sem afirmar
  // o `true` aqui, ela voltaria escondida, com o interruptor do gerenciador dizendo o
  // contrário da tabela. Hoje nenhuma base declara `visible`, mas este util é comum.
  for (const property of pref.order) {
    const column = byProperty.get(property);
    if (!column || placed.has(property)) { continue; }
    placed.add(property);
    ordered.push({ ...column, visible: true });
  }

  // O que a preferência não mencionou: escondido se o usuário desligou, visível se é
  // coluna nova. Mantém a ordem de `base` entre si, que é a ordem pensada pela tela.
  for (const column of copies) {
    if (placed.has(column.property)) { continue; }
    ordered.push({ ...column, visible: !pref.hidden.includes(column.property!) });
  }

  return ordered.map(column => ({ ...column, fixed: pref.fixed.includes(column.property!) }));
}

/**
 * A preferência a salvar, a partir do estado atual da tabela.
 *
 * `visibleProperties` vem do `(p-change-visible-columns)` do po-table — é a única fonte
 * da ORDEM escolhida pelo usuário: as setas do gerenciador reordenam o array interno do
 * po-table (`onVisibleColumnsChange` faz `this.columns = columns`), e como o binding de
 * `p-columns` é de mão única, o array da tela nunca fica sabendo.
 *
 * `visibleProperties` é OPCIONAL porque nem toda mudança do gerenciador vira evento:
 * `allowsEmission` só dispara quando a lista de visíveis muda de conteúdo ou de ordem, e
 * DESAFIXAR uma coluna não muda nem uma nem outra (`emitColumnFixed` a remove e reinsere
 * na mesma posição). Sem o evento, o alfinete removido nunca seria gravado e voltaria no
 * próximo F5 — por isso a saída da tela também salva, aí lendo tudo do array vivo.
 *
 * `current` TEM que ser o array vivo do po-table (`poTable.columns`), nunca o da tela.
 * O alfinete não vem no evento: o gerenciador escreve `fixed` direto nos objetos de
 * coluna — mas, no primeiro liga/desliga, `getVisibleTableColumns` devolve CÓPIAS
 * (`{...column, visible}`) e o po-table troca o array dele por elas. A partir daí é nas
 * cópias que o `fixed` é escrito, e o array da tela guarda objetos que ninguém mais
 * atualiza. Lendo dali, todo pino se perderia depois do primeiro clique num interruptor.
 */
export function toColumnsPref(current: PoTableColumn[], visibleProperties?: string[]): TableColumnsPref {
  const known = current.map(column => column.property!).filter(Boolean);

  // Sem o evento, o próprio array vivo responde: depois de qualquer mexida no
  // gerenciador ele carrega `visible` em toda coluna, e antes disso só as escondidas
  // pela preferência têm a marca — `!== false` cobre os dois casos.
  const visible = visibleProperties
    ? visibleProperties.filter(property => known.includes(property))
    : current.filter(column => column.visible !== false).map(column => column.property!);

  return {
    order: visible,
    hidden: known.filter(property => !visible.includes(property)),
    fixed: current.filter(column => column.fixed).map(column => column.property!)
  };
}
